import { guardString } from "@msuite/utils/strings";
import { Zero } from "@rocicorp/zero";
import {
	ZeroProvider as ZeroSyncProvider,
	type ZeroProviderProps as ZeroSyncProviderProps,
} from "@rocicorp/zero/react";
import { useEffect, useMemo, useRef } from "react";
import { mutators } from "../mutators";
import { schema } from "../schema";

export function ZeroProvider({
	children,
	userId,
	orgId,
	arrangeableTicketProjects,
}: Omit<ZeroSyncProviderProps, "userID"> & {
	userId: string;
	orgId: string;
	/**
	 * The caller's arranging reach, from `Citadel.arrangeableTicketProjects()` — undefined while
	 * the client's rules are still resolving. Read through a ref so a resolved or changed answer
	 * never rebuilds the Zero instance (a rebuild is a full resync).
	 */
	arrangeableTicketProjects?: "all" | readonly string[];
}) {
	const arrangeableTicketProjectsRef = useRef(arrangeableTicketProjects);
	arrangeableTicketProjectsRef.current = arrangeableTicketProjects;

	// The client-side context mirrors what the server derives from the session; it only
	// affects optimistic local evaluation. The server endpoints build their own context and
	// never read this one, so a tampered client gains nothing.
	//
	// No Zero instance exists until an organisation is resolved: one built on a placeholder
	// would sync nothing and be torn down the moment the real id arrives — a wasted resync on
	// every load, and a pointless connection for signed-out sessions. Every component that
	// calls a zero hook mounts behind the app's organisation gate, so rendering the children
	// without the sync provider is safe.
	const zero = useMemo(() => {
		if (!orgId) return null;
		return new Zero({
			schema,
			userID: guardString(userId),
			cacheURL: process.env.PUBLIC_ZERO_PUBLIC_SERVER,
			mutators,
			context: {
				userId,
				orgId,
				arrangeableTicketProjects: () => arrangeableTicketProjectsRef.current,
			},
		});
	}, [userId, orgId]);

	/** Effects */
	// A client that has stopped talking is woken when the tab comes back, and `disconnected` is in
	// that list because it is the state a client reaches on its own and never leaves: the connect
	// attempt is given sixty seconds and is not retried after it. A page opened while zero-cache is
	// still building its replica — which is what a freshly created environment does — therefore
	// stays empty for as long as it is left open, with nothing on screen to say why.
	useEffect(() => {
		if (!zero) return;
		function handleVisibilityChange() {
			if (!zero) return;
			if (document.visibilityState !== "visible") return;
			const state = zero.connection.state.current.name;
			if (state === "needs-auth" || state === "error" || state === "disconnected") {
				zero.connection.connect();
			}
		}
		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, [zero]);

	/** Guard */
	if (!zero) return <>{children}</>;

	/** Render */
	return (
		<ZeroSyncProvider
			userID={userId}
			zero={zero}
		>
			{children as any}
		</ZeroSyncProvider>
	);
}
